import { useState, useEffect } from 'react';


import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import './CompetencyChart.scss';

const CompetencyChart = ({
  competencies,
  selectedDepartmentFilter,
  selectedPositionFilter,
  onFetchEvolution,
  onFetchExpectedScore,
  isLoading
}) => {
  const [selectedCompetency, setSelectedCompetency] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [expectedScore, setExpectedScore] = useState(null);
  const [isChartLoading, setIsChartLoading] = useState(false);

  const safeCompetencies = Array.isArray(competencies) ? competencies : [];

  useEffect(() => {
    setSelectedCompetency(null);
    setTrendData([]);
    setExpectedScore(null);
  }, [selectedDepartmentFilter, selectedPositionFilter]);

  const Skeleton = () => (
    <div className="competency-chart-skeleton">
      {[...Array(4)].map((_, idx) => (
        <div className="skeleton-bar" key={idx}>
          <div className="skeleton-bar-label" />
          <div className="skeleton-bar-fill" />
        </div>
      ))}
    </div>
  );

  const handleCompetencyClick = async (competency) => {
    setSelectedCompetency(competency);
    setIsChartLoading(true);
    let score = null;
    if (competency?.id && typeof onFetchExpectedScore === 'function') {
      score = await onFetchExpectedScore(competency.id);
      setExpectedScore(score);
    } else {
      setExpectedScore(null);
    }
    let evolution = { team: [], company: [] };
    if (competency?.id && typeof onFetchEvolution === 'function') {
      evolution = await onFetchEvolution(competency.id) || { team: [], company: [] };
    }
    const months = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
    const teamSeries = Array.isArray(evolution?.team) ? evolution.team : [];
    const companySeries = Array.isArray(evolution?.company) ? evolution.company : [];
    const companyByKey = {};
    companySeries.forEach(item => { companyByKey[`${item.year}-${item.month}`] = item.average; });
    const sortedTeam = [...teamSeries].sort((a, b) => (a.year - b.year) || (a.month - b.month));
    const trend = sortedTeam.map(item => {
      const companyValue = companyByKey[`${item.year}-${item.month}`];
      return {
        month: months[(item.month ? item.month : 1) - 1] || String(item.month),
        average: typeof item.average === 'number' ? item.average : 0,
        companyAverage: typeof companyValue === 'number' ? companyValue : null,
        expectedScore: score !== null ? Number(score) : null,
        highest: typeof item.max === 'number' ? item.max : 0,
        lowest: typeof item.min === 'number' ? item.min : 0
      };
    });
    setTrendData(trend);
    setIsChartLoading(false);
  };

  const getChartData = () => {
    if (!trendData || trendData.length === 0) return [];
    return trendData.map(item => ({
      ...item,
      expectedScore: expectedScore !== null ? Number(expectedScore) : item.expectedScore
    }));
  };

  const getTitle = () => {
    const baseTitle = `${safeCompetencies.length} competências`;
    if (selectedDepartmentFilter && selectedDepartmentFilter !== 'Ver tudo') {
      return `${baseTitle} - ${selectedDepartmentFilter}`;
    }
    return baseTitle;
  };

  return (
    <div className="competency-chart-card">
      <div className="competency-chart-header">
        <h3 className="competency-chart-title">{getTitle()}</h3>
      </div>
      {isLoading ? (
        <Skeleton />
      ) : (
        <div className="competency-chart-grid">
          <div>
            <div className="competency-list-label">VALOR MÉDIO</div>
            <div className="competency-list">
              {safeCompetencies.map(competency => (
                <div
                  key={competency.name}
                  className={`competency-list-item${selectedCompetency?.name === competency.name ? ' selected' : ''}`}
                  onClick={() => handleCompetencyClick(competency)}
                >
                  <span className="competency-list-item-label">{competency.name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="competency-list-item-value">{competency.averageScore.toFixed(1)}</span>
                    <svg className="competency-arrow" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {isChartLoading ? (
              <div className="competency-chart-loading">
                <div className="competency-chart-loading-spinner"></div>
                <span>Carregando gráfico...</span>
              </div>
            ) : selectedCompetency ? (
              <>
                <div style={{ width: '100%', height: 256 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={getChartData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="month" tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.6)' }} axisLine={{ stroke: 'rgba(255,255,255,0.2)' }} tickLine={{ stroke: 'rgba(255,255,255,0.2)' }} />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.6)' }} axisLine={{ stroke: 'rgba(255,255,255,0.2)' }} tickLine={{ stroke: 'rgba(255,255,255,0.2)' }} />
                      <Tooltip
                        contentStyle={{ background: '#222', border: 'none', borderRadius: 8, color: '#fff' }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(value, name) => {
                          if (name === 'average') return [value, 'Média do time'];
                          if (name === 'companyAverage') return [value, 'Média da empresa'];
                          if (name === 'expectedScore') return [value, 'Nota esperada'];
                          return [value, name];
                        }}
                      />
                      <Line type="monotone" dataKey="average" stroke="#3B82F6" strokeWidth={3} dot={{ fill: '#3B82F6', strokeWidth: 0, r: 4 }} activeDot={{ r: 6, fill: '#3B82F6' }} />
                      <Line type="monotone" dataKey="companyAverage" stroke="#10B981" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: '#10B981', strokeWidth: 0, r: 3 }} activeDot={{ r: 5, fill: '#10B981' }} />
                      <Line type="monotone" dataKey="expectedScore" stroke="#F59E0B" strokeWidth={2} strokeDasharray="8 4" dot={{ fill: '#F59E0B', strokeWidth: 0, r: 3 }} activeDot={{ r: 5, fill: '#F59E0B' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="competency-chart-legend">
                  <div className="competency-chart-legend-item">
                    <div className="competency-chart-legend-dot"></div>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>Média do time</span>
                  </div>
                  <div className="competency-chart-legend-item">
                    <div className="competency-chart-legend-dash-green"></div>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>Média da empresa</span>
                  </div>
                  <div className="competency-chart-legend-item">
                    <div className="competency-chart-legend-dash-orange"></div>
                    <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>Nota esperada</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="competency-chart-empty">
                <p>Clique em uma competência para ver sua evolução</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetencyChart;
